import { Clock } from "lucide-react";
import { useState } from "preact/hooks";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { SheetFooter } from "../ui/sheet";
import { OUTLINE_BRAND_BTN_CLASS } from "./constants";

export interface SubscriptionFooterAction {
  key: string;
  label: string;
  onClick: () => void;
  /** Override the default outline-brand button styling. */
  className?: string;
}

/** How many actions are shown before the "More" button. */
const VISIBLE_COUNT = 3;

export function ManageSubscriptionSheetFooter({
  actions,
  canCancel,
  onCancel,
  cancellationRequested,
  swapPending = false,
  cancelScheduled = false,
  onCancelToday,
  cancelTodayLabel,
  actionPending,
}: {
  actions: SubscriptionFooterAction[];
  canCancel: boolean;
  onCancel: () => void;
  cancellationRequested: boolean;
  /** A PENDING product swap awaiting support confirmation blocks cancel. */
  swapPending?: boolean;
  /** A cancel-after-next-delivery is scheduled: Cancel becomes Cancel today. */
  cancelScheduled?: boolean;
  onCancelToday?: () => void;
  cancelTodayLabel?: string;
  actionPending: boolean;
}) {
  // One-way: once expanded it stays expanded; closing/reopening the sheet
  // unmounts the footer, so it starts collapsed again next time.
  const [expanded, setExpanded] = useState(false);

  // "Other" (contact support) always sits at the very bottom of the More
  // section, below Cancel — so keep it out of the normal visible/hidden split.
  const otherAction = actions.find((a) => a.key === "other");
  const mainActions = actions.filter((a) => a.key !== "other");

  const visible = mainActions.slice(0, VISIBLE_COUNT);
  const hidden = mainActions.slice(VISIBLE_COUNT);
  const hasCancel = cancellationRequested || cancelScheduled || swapPending || canCancel;
  const hasMore = hidden.length > 0 || hasCancel || otherAction != null;

  const renderAction = (action: SubscriptionFooterAction) => (
    <Button
      key={action.key}
      type="button"
      variant="outline"
      disabled={actionPending}
      onClick={action.onClick}
      className={action.className ?? OUTLINE_BRAND_BTN_CLASS}
    >
      {action.label}
    </Button>
  );

  return (
    <SheetFooter className="tw:shrink-0 tw:bg-background tw:flex-col tw:gap-0 tw:pt-0 tw:mt-0">
      <div className="tw:flex tw:flex-col tw:gap-6">
        {visible.map(renderAction)}
        {hasMore && !expanded && (
          <Button
            type="button"
            variant="outline"
            disabled={actionPending}
            onClick={() => setExpanded(true)}
            className={OUTLINE_BRAND_BTN_CLASS}
          >
            More
          </Button>
        )}
      </div>

      {hasMore && (
        <div
          className={cn(
            "tw:grid tw:transition-[grid-template-rows] tw:duration-300 tw:ease-out",
            expanded ? "tw:grid-rows-[1fr]" : "tw:grid-rows-[0fr]",
          )}
        >
          <div className="tw:overflow-hidden">
            <div className="tw:flex tw:flex-col tw:gap-6 tw:pt-6">
              {hidden.map(renderAction)}
              {cancellationRequested ? (
                <div
                  aria-disabled="true"
                  title="Our team is reviewing your cancellation request."
                  className="tw:w-full tw:rounded-md tw:uppercase tw:border tw:border-dashed tw:border-gray-300 tw:bg-gray-50 tw:px-2.5 tw:py-2 tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-gray-500 tw:text-sm tw:font-medium tw:cursor-not-allowed tw:select-none"
                >
                  <Clock className="tw:w-4 tw:h-4" />
                  <span>Cancellation requested</span>
                </div>
              ) : cancelScheduled ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={actionPending}
                  onClick={onCancelToday}
                  className={OUTLINE_BRAND_BTN_CLASS}
                >
                  {cancelTodayLabel}
                </Button>
              ) : swapPending ? (
                <div
                  aria-disabled="true"
                  title="Our team is setting up your product swap. Cancel is unavailable until it's confirmed."
                  className="tw:w-full tw:rounded-md tw:uppercase tw:border tw:border-dashed tw:border-gray-300 tw:bg-gray-50 tw:px-2.5 tw:py-2 tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-gray-500 tw:text-sm tw:font-medium tw:cursor-not-allowed tw:select-none"
                >
                  <Clock className="tw:w-4 tw:h-4" />
                  <span>Swap pending</span>
                </div>
              ) : canCancel ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={actionPending}
                  onClick={onCancel}
                  className={OUTLINE_BRAND_BTN_CLASS}
                >
                  Cancel
                </Button>
              ) : null}
              {otherAction && renderAction(otherAction)}
            </div>
          </div>
        </div>
      )}
    </SheetFooter>
  );
}
